import * as React from 'react';

import { cn, ComboBox, useComboBoxOptions } from '@rematter/ui-web';
import { UseQueryResult } from '@tanstack/react-query';

import { EntityComboBoxProps } from '~/shared/components/select-fields/types';
import { useSearchTerm } from '~/shared/hooks/useSearchTerm';
import { QueryHookResult } from '~/shared/providers/api/createQueryHook';

const createRemotelySearchedEntityComboBox =
  <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Q extends (...args: any[]) => UseQueryResult<T, unknown>,
    T extends { id: string }[],
  >(
    name: string,
    useQueryHook: Q,
    {
      formatLabel,
      formatSubLabel,
      formatGroup,
      compactSelection,
    }: {
      formatLabel: (entity: QueryHookResult<Q>[number]) => string;
      formatSubLabel?: (entity: QueryHookResult<Q>[number]) => string;
      formatGroup?: (entity: QueryHookResult<Q>[number]) => string | undefined;
      compactSelection?: boolean;
    },
  ) =>
  ({
    onChange,
    skipSearch,
    excludeIds,
    ...props
  }: EntityComboBoxProps<QueryHookResult<Q>[number]> & {
    value: string | undefined | null;
    onChange: (
      value: string | undefined,
      entity?: QueryHookResult<Q>[number] | undefined,
    ) => void;
    skipSearch?: boolean;
    excludeIds?: Set<string>;
  } & Parameters<Q>[0]) => {
    const [recentlySelectedEntity, setRecentlySelectedEntity] = React.useState<
      QueryHookResult<Q>[number] | undefined
    >(undefined);

    const { searchTerm, setSearchTerm, debouncedSearchTerm } = useSearchTerm();

    // On initial load, fetch without a text filter. Only send a search term once the user typed 2+ characters.
    const shouldFetch =
      debouncedSearchTerm.length === 0 || debouncedSearchTerm.length >= 2;

    const effectiveSearchTerm =
      debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : undefined;

    const { data: selectedEntities } = useQueryHook({
      ids: props.value ? [props.value] : ['invalid-id'],
      queryOptions: {
        enabled: !!props.value, // Only run query if value exists
      },
    } as never);

    const { data: entities, isFetching } = useQueryHook({
      statuses: props.statuses,
      status: props.status,
      ids: props.ids,
      searchTerm: effectiveSearchTerm,
      facilityId: props.facilityId,
      facilityIDs: props.facilityIDs,
      facilityIds: props.facilityIds,
      accountIds: props.accountIds,
      supplierIds: props.supplierIds,
      customerIds: props.customerIds,
      visibleInPayments: props.visibleInPayments,
      types: props.types,
      customerAccountId: props.customerAccountId,
      processorAccountId: props.processorAccountId,
      itemId: props.itemId,
      lotId: props.lotId,
      queryOptions: {
        enabled: !skipSearch && shouldFetch,
      },
    } as never);

    const options = React.useMemo(
      () =>
        (entities ?? [])
          .filter((entity) => !excludeIds?.has(entity.id))
          .map((entity) => ({
            id: entity.id,
            label: formatLabel(entity),
            subLabel: formatSubLabel?.(entity),
            group: formatGroup?.(entity),
          })),
      [entities, excludeIds],
    );

    const value = React.useMemo(
      () => (props.value ? [props.value] : []),
      [props.value],
    );

    const selection = React.useMemo(() => {
      const selectedEntity =
        selectedEntities?.at(0)?.id === props.value
          ? selectedEntities?.at(0)
          : recentlySelectedEntity;

      return [
        {
          label: selectedEntity ? formatLabel(selectedEntity) : '',
        },
      ];
    }, [props.value, recentlySelectedEntity, selectedEntities]);

    const handleChange = React.useCallback(
      (values: string[]) => {
        const selectedEntity = entities?.find(
          (entity) => entity.id === values.at(0),
        );
        setRecentlySelectedEntity(selectedEntity);
        onChange?.(values.at(0), selectedEntity);
      },
      [entities, onChange],
    );

    return (
      <ComboBox<string>
        {...props}
        query={searchTerm}
        onChangeQuery={setSearchTerm}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={cn(`@${name}`, props.className)}
        options={options}
        isLoading={isFetching}
        value={value}
        onChange={handleChange}
        getSelection={() => selection}
        compactSelection={compactSelection}
      />
    );
  };

const createLocallySearchedEntityComboBox =
  <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Q extends (...args: any[]) => UseQueryResult<T, unknown>,
    T extends { id: string }[],
  >(
    name: string,
    useQueryHook: Q,
    {
      formatLabel,
      formatSubLabel,
      formatGroup,
      compactSelection,
    }: {
      formatLabel: (entity: QueryHookResult<Q>[number]) => string;
      formatSubLabel?: (entity: QueryHookResult<Q>[number]) => string;
      formatGroup?: (entity: QueryHookResult<Q>[number]) => string | undefined;
      compactSelection?: boolean;
    },
  ) =>
  ({
    onChange,
    excludeIds,
    ...props
  }: EntityComboBoxProps<QueryHookResult<Q>[number]> & {
    value: string | undefined | null;
    onChange: (
      value: string | undefined,
      entity?: QueryHookResult<Q>[number] | undefined,
    ) => void;
    excludeIds?: Set<string>;
  } & Parameters<Q>[0]) => {
    const { data: entities, isLoading } = useQueryHook({
      ...props,
    });

    const options = React.useMemo(
      () =>
        (entities ?? [])
          .filter((entity) => !excludeIds?.has(entity.id))
          .map((entity) => ({
            id: entity.id,
            label: formatLabel(entity),
            subLabel: formatSubLabel?.(entity),
            group: formatGroup?.(entity),
          })),
      [entities, excludeIds],
    );

    const { query, onChangeQuery, currentOption, filteredOptions } =
      useComboBoxOptions({
        options,
        value: props.value,
        disabled: props.isDisabled,
        maxResults: 20,
      });

    const value = React.useMemo(
      () => (props.value ? [props.value] : []),
      [props.value],
    );

    const selection = React.useMemo(
      () => [
        {
          label: currentOption?.label,
        },
      ],
      [currentOption],
    );

    const handleChange = React.useCallback(
      (values: string[]) => {
        const selectedEntity = entities?.find(
          (entity) => entity.id === values.at(0),
        );
        onChange?.(values.at(0), selectedEntity);
      },
      [entities, onChange],
    );

    return (
      <ComboBox<string>
        {...props}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={cn(`@${name}`, props.className)}
        query={query}
        onChangeQuery={onChangeQuery}
        options={filteredOptions}
        isLoading={isLoading}
        value={value}
        onChange={handleChange}
        getSelection={() => selection}
        compactSelection={compactSelection}
      />
    );
  };

export const createEntityComboBox = {
  remotelySearched: createRemotelySearchedEntityComboBox,
  locallySearched: createLocallySearchedEntityComboBox,
};
